"use client";

import { useCallback, useEffect, useState } from "react";
import { useMinimapWidth, usePianoHeight } from "@/contexts/studio-context";

/**
 * The one piece to put back under the player's hands.
 *
 * One at a time, or nothing at all — the ceiling lives in the store, which
 * hands out a single offer or `null`. There is no empty state: when nothing
 * stands the line is simply not there. A slot that says it is empty is a slot
 * with a state, and a state invites a count.
 */
export type ResumeOffer = {
  /**
   * `replay` is a kept piece whose silence has run out, `resume` the piece
   * still being climbed. Only the verb and what happens on click differ —
   * both are the same act from the screen's point of view, which is why they
   * share one line rather than sitting on two.
   */
  readonly kind: "replay" | "resume";
  readonly title: string;
  /**
   * The one fact that decides the click, already formatted by the caller:
   * `~2 min` for a replay, `Phrase 7 sur 19` for a piece in progress. `null`
   * when there is nothing certain to say, and then the line says nothing —
   * never a placeholder, which would be a fact about the reader.
   */
  readonly fact: string | null;
};

type EmptyStudioOverlayProps = {
  /**
   * Whether the welcome content is visible. Fades out on first activity
   * (note pressed or click on `joue`) and re-appears after a sustained
   * pause; visibility is owned by the parent. The parent is also
   * responsible for hiding the welcome content during a drag, the
   * drop framing is now rendered by `StudioDropOverlay`.
   */
  visible: boolean;
  /**
   * Whether the player has moved on — played a note, hit play, opened
   * something. Distinct from `visible`, which also goes false while a file is
   * dragged over the roll or an upload errors: those hide the overlay without
   * the player having turned anything down, and using them as the refusal
   * signal declined an offer that was never passed over.
   */
  dismissed: boolean;
  onLoadMidi: () => void;
  onOpenLibrary: () => void;
  onPlay: () => void;
  /**
   * Read once per mount, i.e. exactly when the studio is empty and settled.
   * A callback rather than a value so neither `localStorage` nor the clock
   * is touched during a render.
   */
  readResume: () => ResumeOffer | null;
  /**
   * Awaited: the overlay shows the piece as *opening* until the promise
   * settles. On success it is unmounted by the parent anyway, on failure the
   * line goes back to being an offer — the parent put the offer back, so the
   * click is live again and the screen must say so.
   */
  onResume: () => void | Promise<void>;
  /**
   * The offer was shown and the player did something else instead. Ignoring
   * it has to be free and invisible — there is no ✕, because a refusal with
   * a button is an act, and an act has consequences.
   */
  onDeclineResume: () => void;
};

const COLORS = {
  border: "rgba(255, 255, 255, 0.06)",
  borderHi: "rgba(255, 255, 255, 0.12)",
  text: "#e8e8ed",
  textDim: "#7a7a85",
  textMute: "#4a4a55",
  violet: "#8b5cf6",
  violetLight: "#c4b5fd",
  violetGlow: "rgba(139, 92, 246, 0.32)",
};

/**
 * Empty-state hero, welcome copy with three inline dashed actions
 * ("Charge un morceau" · "parcours la bibliothèque" · "joue").
 *
 * Drag and error states moved to `StudioDropOverlay`, which is mounted
 * independently so the drop affordance is also available when a piece
 * is already loaded. The parent toggles `visible=false` while a drag
 * is in flight so the welcome copy doesn't show through the drop frame.
 */
export function EmptyStudioOverlay({
  visible,
  dismissed,
  onLoadMidi,
  onOpenLibrary,
  onPlay,
  readResume,
  onResume,
  onDeclineResume,
}: EmptyStudioOverlayProps) {
  // Two-phase mount so the *first* render starts at opacity 0 and the
  // CSS transition then plays from 0 → 1. Without it the component
  // pops in instantly when `bootPhase` flips to "ready" right after
  // the jingle.
  const [mounted, setMounted] = useState(false);
  const [resume, setResume] = useState<ResumeOffer | null>(null);
  // « Ouverture de X » entre le clic et l'arrivée du morceau. Tenu ici et
  // non dans la page : c'est la seule surface qui affiche cette ligne, et
  // le seul moment où la page a quelque chose à en dire est déjà la fin de
  // la promesse. Un chargement de bibliothèque ne passe pas par ici.
  const [opening, setOpening] = useState(false);
  const pianoHeight = usePianoHeight();
  const minimapWidth = useMinimapWidth();

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setMounted(true);
      setResume(readResume());
    });

    return () => cancelAnimationFrame(id);
  }, [readResume]);

  // First activity dismisses the overlay. If an offer was standing, that is
  // the refusal: the player did something else, so it is not offered again
  // the next time the studio comes up empty — otherwise "one at a time"
  // becomes "one at a time, on a loop", which is a list served in series.
  //
  // The line itself is **not** taken off the screen. This overlay fades out
  // and comes back on its own every time the player pauses; within one mount
  // it is a single continuous invitation, and the same line is the same
  // offer. Removing it while keeping it rendered — which is what an earlier
  // version did, by clearing the parent's ref but losing the race to clear
  // the line — left something that looked live and did nothing when clicked.
  //
  // Only a `replay` is refused this way. A piece in progress is not on a
  // schedule that could be missed: it is simply where the player is, so
  // passing over it today says nothing about tomorrow and there is nothing
  // to record.
  useEffect(() => {
    if (!dismissed || resume === null || resume.kind !== "replay") {
      return;
    }

    onDeclineResume();
  }, [dismissed, resume, onDeclineResume]);

  const handleResume = useCallback(async () => {
    setOpening(true);

    try {
      await onResume();
    } finally {
      // En cas de succès le parent démonte l'overlay : ce `setState` tombe
      // alors dans le vide, sans avertissement depuis React 18. En cas
      // d'échec, la ligne redevient une offre cliquable.
      setOpening(false);
    }
  }, [onResume]);

  return (
    <div
      aria-hidden={!visible}
      // L'écran s'efface en opacité mais reste monté : il revient dès que le
      // joueur repose les mains. Effacé, il ne doit plus rien être — ni pour
      // le pointeur, ni pour la tabulation, qui atteignait encore « Charge un
      // morceau » sur un écran qu'on ne voit pas.
      inert={!visible}
      className={`absolute transition-opacity duration-500 ease-out ${
        mounted && visible ? "opacity-100" : "opacity-0"
      }`}
      style={{
        // Anchor the overlay to the *roll only*, exclude the bottom
        // piano keyboard and the right-side minimap, both of which the
        // user must keep clickable. Mêmes mesures vivantes que le slot
        // overlay de `StudioFrame`, publiées par la vue Pixi.
        top: 0,
        left: 0,
        right: minimapWidth,
        bottom: pianoHeight,
        // Soft scrim, makes the empty-state copy legible when the user
        // has played a few notes that linger in the roll. Light enough
        // to keep the roll readable through it.
        backgroundColor: "rgba(7, 7, 9, 0.55)",
        backdropFilter: "blur(2px) saturate(80%)",
        WebkitBackdropFilter: "blur(2px) saturate(80%)",
        // Wrapper is non-interactive so background clicks pass through
        // to the piano-roll. Interactive zones (buttons) opt back in
        // via their own `pointer-events: auto`.
        pointerEvents: "none",
      }}
    >
      <IdleContent
        onLoadMidi={onLoadMidi}
        onOpenLibrary={onOpenLibrary}
        onPlay={onPlay}
        resume={resume}
        opening={opening}
        onResume={handleResume}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Idle content, editorial copy with inline dashed actions
// ─────────────────────────────────────────────────────────────

type IdleContentProps = {
  onLoadMidi: () => void;
  onOpenLibrary: () => void;
  onPlay: () => void;
  resume: ResumeOffer | null;
  /** Le morceau proposé est en train de s'ouvrir : l'écran se met en attente. */
  opening: boolean;
  onResume: () => void;
};

/**
 * Le bloc est centré dans la zone du roll, donc ce qui dépasse déborde des
 * deux côtés à la fois : par le haut sur les transports de la toolbar, par le
 * bas sur les touches du piano. Sur un téléphone couché il ne reste qu'une
 * poignée de pixels entre les deux, d'où l'échelle réduite sous viewport court
 * — et la disparition de la ligne des formats, puisqu'on ne dépose pas un
 * fichier par glisser-déposer sur un écran tactile.
 *
 * Les bascules sont écrites en CSS (`[@media(max-height:480px)]:`) et non
 * lues depuis `useShortViewport` : un hook de media query annonce sa valeur
 * mobile au premier rendu, ce qui ferait passer une titraille rapetissée
 * devant un poste de bureau le temps d'une frame.
 */
function IdleContent({ onLoadMidi, onOpenLibrary, onPlay, resume, opening, onResume }: IdleContentProps) {
  /*
    Ce qui prend le pointeur, et rien d'autre.

    Le bloc de titraille fait 880 px de large sur toute la hauteur du roll.
    Lui donner le pointeur posait une plaque au milieu du rouleau : molette et
    glisser y mouraient, le roll cessait de défiler sous le curseur et rien ne
    le disait, puisque l'écran est translucide et s'efface de lui-même dès la
    première note. Le geste appartient au roll partout où il n'y a pas un
    bouton, et un bouton ne fait que quelques dizaines de pixels.

    Ce qu'on y perd, et qui est assumé : sous viewport court, le filet de
    défilement interne ne se prend plus à la molette. La titraille y est déjà
    rapetissée pour tenir, et la tabulation ramène chaque bouton dans le cadre.
  */
  const clickable = { pointerEvents: opening ? "none" : "auto" } as const;

  return (
    <div className="absolute inset-0 flex items-center justify-center px-8 sm:px-12" style={{ pointerEvents: "none" }}>
      {/* Le défilement interne est un filet : la hauteur du roll dépend de
          celle du clavier, donc aucune échelle fixe ne garantit de tenir. */}
      <div
        className="max-h-full overflow-y-auto text-center"
        style={{ pointerEvents: "none", maxWidth: "min(100%, 880px)" }}
      >
        {/* Le même cartouche dit l'état du roll, en attente ou en train de
            recevoir un morceau : le point respire et le mot change, rien ne
            s'ajoute. `aria-live` pour que le changement soit lu. */}
        <div
          aria-live="polite"
          className="mb-[22px] inline-flex items-center transition-colors duration-300 [@media(max-height:480px)]:mb-2.5"
          style={{
            gap: 8,
            padding: "5px 10px",
            borderRadius: 999,
            border: `1px solid ${COLORS.border}`,
            background: "rgba(15,15,20,0.45)",
            fontSize: 10,
            fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
            color: opening ? COLORS.textDim : COLORS.textMute,
            letterSpacing: "0.18em",
          }}
        >
          <span
            aria-hidden="true"
            className={opening ? "animate-pulse motion-reduce:animate-none" : undefined}
            style={{
              width: 5,
              height: 5,
              borderRadius: 99,
              background: opening ? COLORS.violetLight : COLORS.violet,
              boxShadow: `0 0 10px ${COLORS.violetGlow}`,
            }}
          />
          {opening ? "MORCEAU EN ROUTE" : "ROLL EN ATTENTE"}
        </div>

        {/* Les trois portes s'effacent le temps de l'ouverture : l'écran
            est pris, et un second chargement lancé par-dessus le premier
            n'aurait pas de vainqueur prévisible. */}
        <div
          className="text-[32px] transition-opacity duration-300 [@media(max-height:480px)]:text-[19px]"
          style={{
            fontWeight: 250,
            color: COLORS.text,
            letterSpacing: "-0.02em",
            lineHeight: 1.3,
            opacity: opening ? 0.35 : 1,
            // Le retrait pendant l'ouverture est porté par les boutons
            // eux-mêmes (`clickable`) : ce bloc-ci ne prend jamais le
            // pointeur, il laisse passer le geste vers le roll.
            pointerEvents: "none",
          }}
          aria-hidden={opening}
        >
          <div>
            <button
              type="button"
              onClick={onLoadMidi}
              className="cursor-pointer transition-colors"
              style={{
                borderBottom: `1px dashed ${COLORS.violetGlow}`,
                color: COLORS.violetLight,
                padding: "0 2px",
                background: "transparent",
                fontSize: "inherit",
                fontWeight: "inherit",
                letterSpacing: "inherit",
                fontFamily: "inherit",
                ...clickable,
              }}
            >
              Charge un morceau
            </button>
            <span style={{ color: COLORS.textDim, margin: "0 14px" }}>·</span>
            <button
              type="button"
              onClick={onOpenLibrary}
              className="cursor-pointer transition-colors"
              style={{
                borderBottom: `1px dashed ${COLORS.borderHi}`,
                color: COLORS.text,
                padding: "0 2px",
                background: "transparent",
                fontSize: "inherit",
                fontWeight: "inherit",
                letterSpacing: "inherit",
                fontFamily: "inherit",
                ...clickable,
              }}
            >
              parcours la bibliothèque
            </button>
          </div>
          <div>
            <span style={{ color: COLORS.textDim, whiteSpace: "nowrap" }}>
              ou&nbsp;
              <button
                type="button"
                onClick={onPlay}
                className="cursor-pointer transition-colors"
                style={{
                  borderBottom: `1px dashed ${COLORS.borderHi}`,
                  color: COLORS.text,
                  padding: "0 2px",
                  background: "transparent",
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  letterSpacing: "inherit",
                  fontFamily: "inherit",
                  ...clickable,
                }}
              >
                joue
              </button>
              ,&nbsp;tout simplement
            </span>
          </div>
        </div>

        {resume !== null && <ResumeLine offer={resume} opening={opening} onResume={onResume} />}

        <div
          className="mt-7 [@media(max-height:480px)]:hidden"
          style={{
            fontSize: 11.5,
            color: COLORS.textMute,
            fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
            letterSpacing: "0.12em",
          }}
        >
          OU DÉPOSE UN FICHIER <span style={{ color: COLORS.textDim }}>.MID</span> ·{" "}
          <span style={{ color: COLORS.textDim }}>.MUSICXML</span> · <span style={{ color: COLORS.textDim }}>.PDF</span>
        </div>
      </div>
    </div>
  );
}

/**
 * « Rejouer *Gymnopédie n° 1* · ~2 min », or « Reprendre *Gnossienne n° 3* ·
 * Phrase 7 sur 19 ».
 *
 * **Two verbs, because the two acts are opposites.** *Rejouer* for a kept
 * piece: nothing is being caught up on, and `À REPRENDRE` is already the
 * kicker of the work list inside the journey. *Reprendre* for a piece still
 * being climbed, where something genuinely was left mid-way — using
 * *Rejouer* there would promise a piece the player cannot play yet.
 *
 * **The duration, not "read 12 days ago".** The elapsed time would be the
 * only number on the line, therefore its subject, and twelve being larger
 * than seven it reads as a deadline missed however it is phrased. The
 * duration is the fact that decides the click, it is what makes someone say
 * yes at 10 pm, and it is the same fact the piece's own primary action
 * already carries. Where the piece was last played belongs on its record,
 * not on an invitation.
 *
 * **Phrase 7 sur 19 is a position, not a score.** It is the wording « Mes
 * morceaux » already uses (`journeyGlanceLabel`), and it is the one number
 * that says where to walk back in. Not a percentage, which grades the person
 * and cannot be acted on.
 *
 * Quiet on purpose: never the white pill of a primary action. The whole point
 * of a ceiling of one is that the offer is more discreet than everything
 * around it — it offers, it does not direct.
 */
function ResumeLine({ offer, opening, onResume }: { offer: ResumeOffer; opening: boolean; onResume: () => void }) {
  // Le verbe dit ce qui se passe : « Ouverture de X », pas « Reprendre X »
  // avec une roue à côté. Le trait pointillé, qui promet un clic, s'efface
  // avec lui — la ligne n'est plus une offre, c'est un compte rendu.
  const verb = opening ? "Ouverture de" : offer.kind === "replay" ? "Rejouer" : "Reprendre";

  return (
    <div
      aria-busy={opening}
      className="mt-6 text-[15px] [@media(max-height:480px)]:mt-3 [@media(max-height:480px)]:text-[13px]"
      style={{ color: COLORS.textDim }}
    >
      {verb}{" "}
      <button
        type="button"
        onClick={onResume}
        disabled={opening}
        className="transition-[border-color,color] duration-300 enabled:cursor-pointer"
        style={{
          borderBottom: `1px dashed ${opening ? "transparent" : COLORS.violetGlow}`,
          color: COLORS.violetLight,
          padding: "0 2px",
          background: "transparent",
          fontSize: "inherit",
          fontFamily: "inherit",
          // Le bloc qui la porte ne prend pas le pointeur (voir `IdleContent`) :
          // l'offre le réarme sur elle-même, et sur rien d'autre.
          pointerEvents: "auto",
        }}
      >
        {offer.title}
      </button>
      {opening ? (
        <OpeningDots />
      ) : (
        offer.fact !== null && <span style={{ color: COLORS.textMute }}> · {offer.fact}</span>
      )}
    </div>
  );
}

/**
 * Trois points qui respirent tour à tour, à la place du fait de la ligne : le
 * rang de phrase ou la durée n'ont plus rien à décider une fois le clic donné.
 * Même cadence que le point du cartouche, pour que les deux disent la même
 * chose au même rythme — l'un que ça avance, l'autre *sur quoi*.
 */
function OpeningDots() {
  return (
    <span aria-hidden="true" style={{ color: COLORS.textMute }}>
      {" "}
      {[0, 1, 2].map((i) => (
        <span key={i} className="animate-pulse motion-reduce:animate-none" style={{ animationDelay: `${i * 220}ms` }}>
          ·
        </span>
      ))}
    </span>
  );
}
